export interface PublicKeyCredentialRequestOptionsJSON {
    challenge: string;
    rpId?: string;
    timeout?: number;
    userVerification?: UserVerificationRequirement;
    allowCredentials?: Array<{
        id: string;
        type: PublicKeyCredentialType;
        transports?: string[];
    }>;
}

export interface SerializedAssertionCredential {
    id: string;
    rawId: string;
    type: string;
    authenticatorAttachment: string | null;
    clientExtensionResults: AuthenticationExtensionsClientOutputs;
    response: {
        authenticatorData: string;
        clientDataJSON: string;
        signature: string;
        userHandle: string | null;
    };
}

export function bytesToBase64Url(bytes: Uint8Array): string {
    let binary = '';
    for (const byte of bytes) binary += String.fromCharCode(byte);
    return btoa(binary)
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/u, '');
}

export function base64UrlToBytes(value: string): Uint8Array<ArrayBuffer> {
    const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    const binary = atob(padded);
    return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}

export function base64UrlToArrayBuffer(value: string): ArrayBuffer {
    return base64UrlToBytes(value).buffer;
}

export async function sha256Base64Url(bytes: Uint8Array): Promise<string> {
    const source = Uint8Array.from(bytes).buffer;
    const digest = await crypto.subtle.digest('SHA-256', source);
    return bytesToBase64Url(new Uint8Array(digest));
}

export async function sha256Hex(bytes: Uint8Array): Promise<`0x${string}`> {
    const source = Uint8Array.from(bytes).buffer;
    const digest = new Uint8Array(
        await crypto.subtle.digest('SHA-256', source),
    );
    return `0x${Array.from(digest, (byte) =>
        byte.toString(16).padStart(2, '0'),
    ).join('')}`;
}

export function parseRequestOptions(
    options: PublicKeyCredentialRequestOptionsJSON,
): PublicKeyCredentialRequestOptions {
    return {
        ...options,
        challenge: base64UrlToArrayBuffer(options.challenge),
        allowCredentials: options.allowCredentials?.map((credential) => ({
            id: base64UrlToArrayBuffer(credential.id),
            type: credential.type,
            transports: credential.transports as
                | AuthenticatorTransport[]
                | undefined,
        })),
    };
}

export function serializePublicKeyCredential(
    credential: PublicKeyCredential,
): SerializedAssertionCredential {
    const response = credential.response as AuthenticatorAssertionResponse;
    return {
        id: credential.id,
        rawId: bytesToBase64Url(new Uint8Array(credential.rawId)),
        type: credential.type,
        authenticatorAttachment: credential.authenticatorAttachment,
        clientExtensionResults: credential.getClientExtensionResults(),
        response: {
            authenticatorData: bytesToBase64Url(
                new Uint8Array(response.authenticatorData),
            ),
            clientDataJSON: bytesToBase64Url(
                new Uint8Array(response.clientDataJSON),
            ),
            signature: bytesToBase64Url(new Uint8Array(response.signature)),
            userHandle: response.userHandle
                ? bytesToBase64Url(new Uint8Array(response.userHandle))
                : null,
        },
    };
}

export async function getPasskeyAssertion(
    options: PublicKeyCredentialRequestOptionsJSON,
): Promise<SerializedAssertionCredential> {
    const credential = await navigator.credentials.get({
        publicKey: parseRequestOptions(options),
    });
    if (!(credential instanceof PublicKeyCredential)) {
        throw new Error('No passkey assertion was returned.');
    }
    return serializePublicKeyCredential(credential);
}
